import * as THREE from 'three';
import { ENV, AGM65 } from '../core/SimConfig.js';
import { MissileState } from './AIM9Missile.js';

// ── Reusable temp scratch (zero-alloc hot path) ──────────────────────────────
const _worldPos = new THREE.Vector3();
const _worldQuat = new THREE.Quaternion();
const _worldScale = new THREE.Vector3();
const _aimPoint = new THREE.Vector3();
const _r = new THREE.Vector3();
const _vRel = new THREE.Vector3();
const _aimDir = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _lateral = new THREE.Vector3();
const _accel = new THREE.Vector3();
const _vNorm = new THREE.Vector3();
const NEG_Z = new THREE.Vector3(0, 0, -1);

// Drag pre-factor: 0.5 * rho * Cd * A / mass
const DRAG_K = 0.5 * ENV.airDensity * AGM65.dragCd * AGM65.bodyArea / AGM65.mass;
const THRUST_ACCEL = AGM65.thrust / AGM65.mass;
const FLAME_SPIN_RATE = 30; // rad/s about flame's local Y

/**
 * One AGM-65 Maverick. Mirrors {@link AIM9Missile}'s mount → fire → free-flight
 * lifecycle, but is an air-to-ground weapon: instead of an IR seeker hunting the
 * nearest air target, it flies toward a player-designated point. That point is
 * either a moving enemy entity (locked at fire time) or a fixed world position
 * on the terrain.
 *
 * Hit detection (swept vs target hit-volumes and terrain) is run by WeaponSystem
 * just like the AIM-9; this class owns only guidance + integration.
 */
export default class AGM65Missile {
  /**
   * @param {{ pylonNode: THREE.Object3D, modelMesh: THREE.Object3D, scene: THREE.Scene }} opts
   */
  constructor({ pylonNode, modelMesh, scene }) {
    this.pylonNode = pylonNode;
    this.scene = scene;

    // Same pylon-empty orientation fix as the AIM-9: the Blender empties point
    // their arrow along local +Z, while the missile model is nose-along -Z, so
    // +90° about local X tips the nose from pylon-down to pylon-forward.
    this.mesh = modelMesh;
    this.mesh.position.set(0, 0, 0);
    this.mesh.rotation.set(Math.PI / 2, 0, 0);
    pylonNode.add(this.mesh);

    this.flameMesh = this.mesh.getObjectByName(AGM65.flameMesh) ?? null;
    if (this.flameMesh) this.flameMesh.visible = false;

    this.state = MissileState.MOUNTED;

    // Free-flight physics state — populated on fire().
    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();
    this.age = 0;

    // Designation: a locked enemy entity (preferred while alive) and a fixed
    // fallback world point. One or both are set at fire time.
    this.targetEntity = null;
    this.targetPoint = new THREE.Vector3();

    // Swept-hit bookkeeping (read by WeaponSystem).
    this.prevX = 0; this.prevY = 0; this.prevZ = 0;

    // Set before an internal kill() so WeaponSystem can spawn the explosion.
    this.detonation = null;

    // Smoke-trail emitter handle (assigned by WeaponSystem on fire).
    this.smokeTrailHandle = -1;
  }

  /**
   * Detach from the pylon, capture world transform as initial state, and begin
   * free-flight toward the designated target.
   *
   * @param {object|null} targetEntity — enemy with .flight.position / .hitVolume (or null)
   * @param {THREE.Vector3} targetPoint — fixed world fallback aim point
   * @param {THREE.Vector3} aircraftVelocity — initial inertial velocity
   */
  fire(targetEntity, targetPoint, aircraftVelocity) {
    if (this.state !== MissileState.MOUNTED) return;

    this.mesh.updateWorldMatrix(true, false);
    this.mesh.matrixWorld.decompose(_worldPos, _worldQuat, _worldScale);

    this.pylonNode.remove(this.mesh);
    this.scene.add(this.mesh);
    this.mesh.position.copy(_worldPos);
    this.mesh.quaternion.copy(_worldQuat);

    this.position.copy(_worldPos);
    this.launchPosition = _worldPos.clone();
    this.quaternion.copy(_worldQuat);
    this.velocity.copy(aircraftVelocity);
    this.age = 0;
    this.targetEntity = targetEntity;
    this.targetPoint.copy(targetPoint);
    this.state = MissileState.FIRED;

    if (this.flameMesh) {
      this.flameMesh.visible = true;
      this.flameMesh.rotation.y = 0;
    }
  }

  /** Integrate one step of free-flight physics + PN guidance. No-op unless FIRED. */
  update(dt) {
    if (this.state !== MissileState.FIRED) return;

    this.age += dt;
    if (this.age > AGM65.maxLifetime) {
      this.detonation = { isAircraftHit: false };
      this.kill();
      return;
    }

    // ── Resolve the live aim point: track the locked entity while it lives,
    //    otherwise keep flying at the last-known / static ground point. ────────
    const ent = this.targetEntity;
    const entAlive = ent && !ent.dead && !ent.destroyed;
    if (entAlive) {
      const tp = ent.hitVolume?.center ?? ent.flight?.position;
      if (tp) this.targetPoint.copy(tp);
    }
    _aimPoint.copy(this.targetPoint);

    // ── Guidance: lead pursuit using closing rate (suppressed until clear of
    //    the launching aircraft). ───────────────────────────────────────────
    let steerX = 0, steerY = 0, steerZ = 0;
    const dx = this.position.x - this.launchPosition.x;
    const dy = this.position.y - this.launchPosition.y;
    const dz = this.position.z - this.launchPosition.z;
    const separated = (dx * dx + dy * dy + dz * dz) > (AGM65.steerArmDistance * AGM65.steerArmDistance);

    if (separated) {
      _r.copy(_aimPoint).sub(this.position);
      // Relative velocity vs a (possibly moving) target.
      const tVel = entAlive ? ent.flight?.velocity : null;
      _vRel.set(tVel ? tVel.x : 0, tVel ? tVel.y : 0, tVel ? tVel.z : 0).sub(this.velocity);

      const range = _r.length();
      if (range > 0.001) {
        const closing = -(_r.x * _vRel.x + _r.y * _vRel.y + _r.z * _vRel.z) / range;
        const tLead = range / Math.max(closing, 50);
        _aimDir.copy(_r);
        if (tVel) _aimDir.addScaledVector(_vRel, tLead); // lead a moving target
        _aimDir.normalize();

        const speed = this.velocity.length();
        if (speed > 1) {
          _fwd.copy(this.velocity).divideScalar(speed);
          const along = _aimDir.dot(_fwd);
          _lateral.copy(_aimDir).addScaledVector(_fwd, -along);
          const latMag = _lateral.length();
          if (latMag > 1e-4) {
            _lateral.divideScalar(latMag);
            const angle = Math.atan2(latMag, along);
            let aMag = AGM65.navConstant * Math.max(closing, speed * 0.5) * angle;
            if (aMag > AGM65.maxLateralAccel) aMag = AGM65.maxLateralAccel;
            steerX = _lateral.x * aMag;
            steerY = _lateral.y * aMag;
            steerZ = _lateral.z * aMag;
          }
        }

        // Proximity fuse vs a locked entity — analytic CPA over the frame, so a
        // fast pass doesn't tunnel past a small hitbox (mirrors the AIM-9).
        if (entAlive) {
          const fuseCenter = ent.hitVolume?.center ?? ent.flight.position;
          const tv = ent.flight?.velocity;
          const tvx = tv ? tv.x : 0, tvy = tv ? tv.y : 0, tvz = tv ? tv.z : 0;
          const r0x = fuseCenter.x - tvx * dt - this.position.x;
          const r0y = fuseCenter.y - tvy * dt - this.position.y;
          const r0z = fuseCenter.z - tvz * dt - this.position.z;
          const vRx = tvx - this.velocity.x;
          const vRy = tvy - this.velocity.y;
          const vRz = tvz - this.velocity.z;
          const vR2 = vRx * vRx + vRy * vRy + vRz * vRz;
          let tStar = 0;
          if (vR2 > 1e-4) {
            tStar = -(r0x * vRx + r0y * vRy + r0z * vRz) / vR2;
            if (tStar < 0) tStar = 0;
            else if (tStar > dt) tStar = dt;
          }
          const cpaX = r0x + vRx * tStar;
          const cpaY = r0y + vRy * tStar;
          const cpaZ = r0z + vRz * tStar;
          if (cpaX * cpaX + cpaY * cpaY + cpaZ * cpaZ < AGM65.proximityFuse * AGM65.proximityFuse) {
            const detX = this.position.x + this.velocity.x * tStar;
            const detY = this.position.y + this.velocity.y * tStar;
            const detZ = this.position.z + this.velocity.z * tStar;
            ent.onProjectileHit?.(detX, detY, detZ, AGM65.damage, 'agm65');
            this.position.set(detX, detY, detZ);
            this.detonation = { isAircraftHit: true };
            this.kill();
            return;
          }
        }
      }
    }

    // ── Body forward (along velocity) ─────────────────────────────────────
    const speed = this.velocity.length();
    if (speed > 0.01) {
      _vNorm.copy(this.velocity).divideScalar(speed);
    } else {
      _vNorm.set(0, 0, -1).applyQuaternion(this.quaternion);
    }

    // ── Forces ────────────────────────────────────────────────────────────
    const burning = this.age < AGM65.burnTime;
    _accel.set(0, 0, 0);
    if (burning) _accel.addScaledVector(_vNorm, THRUST_ACCEL);

    if (this.flameMesh) {
      if (burning) {
        this.flameMesh.rotation.y += FLAME_SPIN_RATE * dt;
      } else if (this.flameMesh.visible) {
        this.flameMesh.visible = false;
      }
    }

    if (speed > 0.01) {
      const dragMag = DRAG_K * speed * speed;
      _accel.addScaledVector(_vNorm, -dragMag);
    }

    _accel.y -= ENV.gravity;
    _accel.x += steerX;
    _accel.y += steerY;
    _accel.z += steerZ;

    // ── Semi-implicit Euler ───────────────────────────────────────────────
    this.velocity.addScaledVector(_accel, dt);

    this.prevX = this.position.x;
    this.prevY = this.position.y;
    this.prevZ = this.position.z;

    this.position.addScaledVector(this.velocity, dt);

    // ── Orient mesh: nose along velocity (model is Z-forward → -Z) ──────────
    if (this.velocity.lengthSq() > 1) {
      _vNorm.copy(this.velocity).normalize();
      this.quaternion.setFromUnitVectors(NEG_Z, _vNorm);
    }
    this.mesh.position.copy(this.position);
    this.mesh.quaternion.copy(this.quaternion);
  }

  /** Detach mesh and mark dead. WeaponSystem prunes DEAD missiles. */
  kill() {
    if (this.state === MissileState.DEAD) return;
    if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
    this.state = MissileState.DEAD;
    this.targetEntity = null;
  }

  dispose() {
    this.kill();
  }
}
